import { useRef, useState } from 'react';
import { bt, btp } from '../../i18n';
import type { VideoField, VideoValue } from '@aquilixshop/ui-react';
import { FieldRow } from '../FieldRow';
import { useFieldTarget } from '../fieldTarget';
import { acceptVideoFile, videoFromTransfer, WARN_VIDEO_BYTES } from './videoUpload';
import { uploadImage, uploadVideo, type VideoQuality } from '../../lib/uploadAsset';
import {
  cancelMediaUpload,
  localPreviewFor,
  startMediaUpload,
  useMediaUpload,
  type UploadPhase,
} from '../../lib/mediaUploads';
import { formatElapsed, useElapsedSeconds } from '../../lib/elapsed';

/**
 * Video control (spec 0020): UPLOAD / DRAG-DROP / PASTE ONLY — never a URL field (project rule). Validates
 * type + size + magic bytes and reads width/height/duration (videoUpload.ts), UPLOADS the raw file to
 * `POST /api/assets/videos` (octet-stream, streamed) and stores a `VideoValue` with a single `sources[]`
 * entry pointing at the returned delivery URL. Commits immediately; clearing sets the value to undefined.
 *
 * Like `ImageControl`, the upload runs in the background registry (`lib/mediaUploads.ts`) and NOT in this
 * component (founder, 2026-09-15). It matters more here than anywhere: a 30 MB clip is ~2.5 minutes on a
 * home uplink, and closing the panel used to take the progress bar, the Cancel button and any failure
 * message down with it — while the bytes carried on with nobody left to tell. The whole job, poster
 * included, now outlives the panel; re-opening re-attaches to it.
 */
/**
 * Long edge of the phone rendition the backend produces (`FfmpegVideoProcessor.SMALL_LONG_EDGE`). Stored on
 * the source so the storefront engine can compare it against the painted width — keep the two in step.
 */
/**
 * Last-resort width for the phone rendition, used ONLY when the server reported none (no ffmpeg).
 *
 * It is a fallback and not a fact: the encoder scales the LONG edge to 640, so this is the real width of a
 * landscape clip's rendition and a lie about a portrait one's (360). The server now probes the stored file
 * and sends `smallWidth`; that is what gets written to the document whenever it is there.
 */
const SMALL_RENDITION_WIDTH = 640;

export function VideoControl({
  field,
  value,
  onChange,
}: {
  field: VideoField;
  value: VideoValue | undefined;
  onChange: (next: VideoValue | undefined) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  /** Rejections from the CLIENT GATE only (wrong type, too big, unreadable) — instant, local, no job yet. */
  const [rejected, setRejected] = useState<string | undefined>();
  const [warn, setWarn] = useState<string | undefined>();
  const target = useFieldTarget(field.id);
  const job = useMediaUpload(target.key);
  const busy = job !== undefined && job.phase !== 'error';
  const progress = job?.percent ?? 0;
  const retrying = job?.retrying ?? 0;
  const elapsed = useElapsedSeconds(busy ? job.startedAt : undefined);
  const error = rejected ?? job?.error;
  /**
   * How hard the server may squeeze the clip. `'standard'` by default — a light file is the right default
   * for a storefront, and it is what every upload did before this choice existed.
   *
   * THREE positions, not two, and the middle one is the point. The switch this replaces offered "720p at
   * CRF 26" or "your own bytes, and no phone rendition at all" — measured VMAF 82 against a heavy page,
   * with nothing between them. A product clip of a SHOE fell in that gap: too detailed for the crush, too
   * ordinary to justify shipping the original to every visitor. `'detail'` is 1080p at CRF 21, which
   * scores 95 on the same plan and still cuts a phone rendition.
   *
   * It is READ at upload time and never stored on the block: what it produces is bytes in object storage,
   * not a page setting. Changing it later does nothing until the seller uploads again, which is why it
   * only shows on the drop zone — next to the act it actually governs.
   */
  const [quality, setQuality] = useState<VideoQuality>('standard');

  /**
   * Prefer, in order: the clip being uploaded right now (the seller's own file, from their disk), then the
   * local blob kept from an upload this tab already finished — previewing that instead of the remote
   * delivery URL is the difference between an instant preview and re-downloading, from the object store, a
   * file that is still in memory here — and finally the stored URL (a document loaded from the server).
   *
   * All three now survive this component: the blobs are owned by the registry, not by a ref that died with
   * the panel.
   */
  const stored = value?.sources?.[0]?.url;
  const src = job?.previewUrl ?? localPreviewFor(stored) ?? stored;

  const accept = async (file: File | null) => {
    if (!file || busy) return;
    setRejected(undefined);
    setWarn(
      file.size > WARN_VIDEO_BYTES
        ? bt('chrome.media.video_large', 'Large video — a shorter, compressed clip loads faster.')
        : undefined,
    );
    // Read at PICK time, not inside the job: the tier belongs to the file the seller is sending now.
    const wantsQuality = quality;
    // Captured BEFORE the optimistic write below; the registry decides when to put it back (`revert`).
    const previous = value;
    startMediaUpload({
      key: target.key,
      kind: 'video',
      fileName: file.name,
      reveal: target.reveal,
      revert: () => onChange(previous),
      describeError: (e) =>
        e instanceof Error
          ? e.message
          : bt('chrome.media.video_failed', 'Could not add that video.'),
      // Everything below runs in the registry. The seller can close this panel, open another section, or
      // go and edit a different page — the clip keeps going and lands in the right block when it arrives.
      run: async (handle) => {
        const meta = await acceptVideoFile(file); // client gate + dims/duration + local preview URL
        handle.ownPreview(meta.previewUrl);
        // THE CANVAS TAKES IT NOW — and here it matters more than anywhere: a 30 MB clip is minutes on a
        // home uplink, and the block stayed empty for every one of them. The seller's own file plays from
        // their disk immediately, at its real geometry, and the delivery URLs replace it when they land.
        // `lib/localMedia.ts` keeps this URL out of every save; `revert` puts the slot back on failure.
        onChange({
          sources: [
            { url: meta.previewUrl, type: meta.mime, width: meta.width, height: meta.height },
          ],
          poster: value?.poster,
          duration: meta.duration || undefined,
        });
        const uploaded = await uploadVideo(file, {
          onProgress: handle.setPercent,
          signal: handle.signal,
          onRetry: handle.setRetrying,
          quality: wantsQuality,
        });

        // Store the locally-grabbed still as a real asset. BEST EFFORT on purpose: a poster is a speed
        // optimisation, so a failure here must cost the seller their video upload under no circumstances —
        // we simply keep whatever poster was there before.
        // The server extracts the poster with ffmpeg during optimisation — guaranteed, and already stored.
        // Only fall back to uploading the browser's own capture when it didn't (local dev has no ffmpeg),
        // which also saves the seller a second trip over their uplink in the normal case.
        let poster = uploaded.posterUrl ?? value?.poster;
        if (!uploaded.posterUrl && meta.poster) {
          try {
            const up = await uploadImage(
              new File([meta.poster], 'poster.webp', { type: 'image/webp' }),
            );
            poster = up.url;
          } catch {
            /* keep the previous poster (or none) — the video itself is already safely uploaded */
          }
        }

        // Renditions, smallest first, each carrying its pixel width — the storefront engine promotes the one
        // that fits the box the clip is painted in, so a phone stops downloading the desktop file. The small
        // one is absent when the server judged a second copy pointless (clip already phone-sized, no ffmpeg).
        // WIDTHS COME FROM THE SERVER, which probed the files it actually stored. What this browser
        // measured (`meta`) describes the file BEFORE the pipeline touched it, and after a re-encode that
        // is a file nobody will ever download — measured on a live clip: the document said 1080 for a
        // stored 720, and 640 for a phone rendition that is 360 wide (the encoder scales the LONG edge, so
        // a portrait clip comes out 360x640). The storefront picks the rendition by exactly these numbers,
        // so it handed the 360 px file to boxes asking for up to 640 and the visitor saw an upscale — while
        // this panel, which previews the seller's own local file, showed the clip pristine. `meta` stays as
        // the fallback for a server with no ffmpeg, where nothing is re-encoded and it is therefore right.
        const small = uploaded.smallUrl
          ? [
              {
                url: uploaded.smallUrl,
                type: meta.mime,
                width: uploaded.smallWidth ?? SMALL_RENDITION_WIDTH,
                height: uploaded.smallHeight,
              },
            ]
          : [];
        // The MIDDLE rung, added 2026-09-16. Below it there was 640 and above it the stored file, with
        // nothing between — so every visitor who was not on a phone had one option: the biggest file on
        // the shelf. Measured on a live hero, that was 4.18 MB for four seconds where 200 kB covers the
        // box, which is the "the video is too slow on desktop, it freezes" report. Absent when the server
        // judged the clip already near this size, exactly like the phone rung.
        const mid = uploaded.midUrl
          ? [
              {
                url: uploaded.midUrl,
                type: meta.mime,
                width: uploaded.midWidth,
                height: uploaded.midHeight,
              },
            ]
          : [];
        // Ownership of the blob moves to the keepsake map, filed under the url the panel will preview, so
        // the picture stays instant AND survives the panel being closed — where the old ref could do neither.
        handle.keepPreviewFor(small[0]?.url ?? uploaded.url);
        onChange({
          sources: [
            ...small,
            ...mid,
            {
              url: uploaded.url,
              type: meta.mime,
              width: uploaded.width ?? meta.width,
              height: uploaded.height ?? meta.height,
            },
          ],
          poster,
          duration: meta.duration || undefined,
        });
      },
    });
  };

  const clear = () => {
    cancelMediaUpload(target.key);
    setRejected(undefined);
    setWarn(undefined);
    onChange(undefined);
  };

  return (
    <FieldRow id={field.id} label={field.label} info={field.info} error={error} labelAs="span">
      {/* Same flex column as ImageControl: the preview and the large-file warning must not touch. */}
      <div className="bld-image">
        {src ? (
          <div className="bld-image__preview">
            {/*
            The inspector thumbnail must cost NOTHING to display.
            With a poster we show that still and set `preload="none"`: the clip is not fetched at all until
            the seller presses play. That is the fix for "the video takes forever to appear in the panel" —
            there is nothing left to wait for.
            Without a poster (a clip uploaded before posters were captured) we fall back to
            `preload="metadata"`, which is still far better than the browser default: Safari desktop treats
            a missing attribute as `auto` and pulls the ENTIRE file for a thumbnail nobody asked to play.
          */}
            <video
              src={src}
              poster={value?.poster}
              muted
              loop
              playsInline
              controls
              preload={value?.poster ? 'none' : 'metadata'}
              style={{ inlineSize: '100%', display: 'block' }}
            />
            {busy ? (
              /* The progress now rides ON the preview, the way it does for images: the seller watches
                 their own clip while it travels, instead of a bare box with a percentage. */
              <div className="bld-image__sending">
                {/* Same two honest pictures as the image control: indeterminate until the transport has
                    actually measured something, then the real width — shimmering once every byte is in
                    the socket, because that wait IS the transfer on a home uplink. */}
                <span
                  className={
                    job?.reported ? 'bld-progress' : 'bld-progress bld-progress--indeterminate'
                  }
                  role="progressbar"
                  aria-valuenow={job?.reported ? progress : undefined}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={bt('chrome.media.progress', 'Upload progress')}
                >
                  {/* Keeps MOVING once the bytes are in the socket — that phase IS the transfer on a
                      home uplink, and a static bar there is what read as "hung". */}
                  <span
                    className={
                      job?.phase === 'processing'
                        ? 'bld-progress__bar bld-progress__bar--waiting'
                        : 'bld-progress__bar'
                    }
                    style={{ inlineSize: job?.reported ? `${progress}%` : '0%' }}
                  />
                </span>
                <span role="status">
                  {uploadLabel(progress, retrying, quality, job?.phase, job?.reported ?? false)}
                </span>
                {elapsed > 0 && (
                  <span className="bld-image__sending-time">{formatElapsed(elapsed)}</span>
                )}
                <span className="bld-image__sending-note">
                  {bt('chrome.media.keeps_going', 'Keeps going if you close this panel.')}
                </span>
                <button
                  type="button"
                  className="bld-btn bld-btn--ghost"
                  onClick={() => cancelMediaUpload(target.key)}
                >
                  {bt('chrome.media.cancel', 'Cancel')}
                </button>
              </div>
            ) : (
              <button type="button" className="bld-iconbtn bld-image__clear" onClick={clear}>
                {bt('chrome.media.remove', 'Remove')}
              </button>
            )}
          </div>
        ) : (
          <div
            className={over ? 'bld-image__drop is-over' : 'bld-image__drop'}
            role="button"
            tabIndex={0}
            aria-label={btp('chrome.media.upload_aria', 'Upload {name}', { name: field.label })}
            aria-busy={busy}
            onClick={() => !busy && inputRef.current?.click()}
            onKeyDown={(e) => {
              if (!busy && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (!busy) setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              void accept(
                videoFromTransfer(e.dataTransfer.items) ?? e.dataTransfer.files[0] ?? null,
              );
            }}
            onPaste={(e) => void accept(videoFromTransfer(e.clipboardData.items))}
          >
            {busy ? (
              /* Only visible in the sliver before the local preview exists (the browser is still decoding
                 the clip's metadata); once it does, the progress moves onto the preview above. */
              <strong>
                {uploadLabel(progress, retrying, quality, job?.phase, job?.reported ?? false)}
              </strong>
            ) : (
              <>
                <strong>{bt('chrome.media.upload_video', 'Upload a video')}</strong>
                <span>
                  {bt('chrome.media.video_hint', 'MP4 or WebM · click, drag & drop, or paste')}
                </span>
              </>
            )}
          </div>
        )}

        {/* A failed upload leaves the seller holding a one-line reason (FieldRow renders it) and the ONE
            action that follows from it. Without this they have to re-open the OS file browser and find the
            same clip again — for a failure that was ours, on bytes we already had. */}
        {job?.phase === 'error' && (
          <button type="button" className="bld-btn bld-btn--ghost" onClick={() => job.retry()}>
            {bt('chrome.media.try_again', 'Try again')}
          </button>
        )}

        {warn && !error && (
          <p className="bld-field__hint" role="status">
            {warn}
          </p>
        )}

        {/*
          The QUALITY choice, and it sits here — on the drop zone, next to the act it governs — because it
          is read when the file is sent and never afterwards. Once a clip is in place the control would be
          inert, so it is not shown: changing it means Remove, then upload again.

          Each tier's hint names what it COSTS, not just what it gives, because that is the half a seller
          cannot see until a customer complains: Standard says the picture softens, Detail says phones
          download more, Original says every visitor carries the whole file. A control that only said
          "optimize" hid all three.
        */}
        {!src && !busy && (
          <div className="bld-video__quality">
            <span className="bld-video__quality-label" id={`${field.id}-quality-label`}>
              {bt('chrome.media.quality', 'Video quality')}
            </span>
            <div
              className="bld-seg"
              role="radiogroup"
              aria-labelledby={`${field.id}-quality-label`}
            >
              {QUALITY_TIERS.map((tier) => (
                <button
                  key={tier}
                  type="button"
                  role="radio"
                  aria-checked={quality === tier}
                  className="bld-seg__opt"
                  onClick={() => setQuality(tier)}
                >
                  {qualityName(tier)}
                </button>
              ))}
            </div>
            <span className="bld-field__hint">{qualityHint(quality)}</span>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,video/webm"
          style={{ display: 'none' }}
          onChange={(e) => {
            void accept(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />
      </div>
    </FieldRow>
  );
}

/**
 * The tiers in the order they are offered — lightest first, so the row reads as a dial from "fast page" to
 * "my own file" and the default sits where a seller's eye lands first.
 */
const QUALITY_TIERS: readonly VideoQuality[] = ['standard', 'detail', 'original'];

/** The tier's name on the segmented control. Short: three of them share one 320 px row. */
function qualityName(tier: VideoQuality): string {
  switch (tier) {
    case 'detail':
      return bt('chrome.media.quality_detail', 'Detail');
    case 'original':
      return bt('chrome.media.quality_original', 'Original');
    case 'standard':
      return bt('chrome.media.quality_standard', 'Standard');
  }
}

/**
 * The sentence under the row, for the SELECTED tier only.
 *
 * Every one of them names a cost. That is deliberate and it is the lesson of the switch this replaces: a
 * seller cannot see what they are trading away until a customer tells them, so the control has to say it.
 * Standard costs sharpness, Detail costs phone bytes (a 3x phone needs ~1170 px, so it walks past the 640
 * rendition and takes the 1080p file), Original costs every visitor the whole upload.
 */
function qualityHint(tier: VideoQuality): string {
  switch (tier) {
    case 'detail':
      return bt(
        'chrome.media.quality_detail_hint',
        'Full 1080p, lightly compressed — for a product clip where the details matter. Phones download more.',
      );
    case 'original':
      return bt(
        'chrome.media.quality_original_hint',
        'Your file, untouched — that is what a large screen gets. Phones and slow connections get a lighter copy.',
      );
    case 'standard':
      return bt(
        'chrome.media.quality_standard_hint',
        'Re-encoded lighter (720p) — the page loads fast. Fine detail softens.',
      );
  }
}

/**
 * What the seller reads while the clip travels. Three honest states, in the order they happen: a dropped
 * connection being re-sent, the wait after the last byte went into the socket, and the ordinary percentage.
 *
 * The second state is keyed off the PHASE, never off `progress >= 100`: since 2026-09-15 the bar stops at
 * [SENT_CAP] so that a full bar can only ever mean "landed" (`xhr.upload.onprogress` counts bytes handed
 * to the socket, not bytes received — on a small body it fills in one jump while the file has not begun to
 * climb the uplink). A label that waited for 100 here would simply never appear.
 *
 * With the re-encode refused ('original') there is nothing to optimise — the server is only moving the MP4
 * index, about a second — so saying "Optimizing video…" there would name work we were explicitly told not
 * to do.
 */
function uploadLabel(
  progress: number,
  retrying: number,
  quality: VideoQuality,
  phase: UploadPhase | undefined,
  reported: boolean,
): string {
  if (retrying > 0 && phase !== 'processing') {
    return btp('chrome.media.retrying', 'Connection dropped — retrying ({n}/3)… {pct}%', {
      n: retrying,
      pct: progress,
    });
  }
  if (phase === 'processing') {
    return quality === 'original'
      ? bt('chrome.media.finishing', 'Finishing up…')
      : bt('chrome.media.optimizing', 'Optimizing video…');
  }
  // No measurement yet (`upload.onprogress` is not guaranteed to fire) → say the true thing without a
  // figure, rather than a determinate "0 %" nobody measured.
  if (!reported) return bt('chrome.media.uploading', 'Uploading…');
  return btp('chrome.media.uploading_pct', 'Uploading… {pct}%', { pct: progress });
}
